import {
  ChangeDetectionStrategy,
  Component,
  input,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';

type SortColumn = 'applicationName' | 'assignedTo' | 'archivalRequired';
type SortDirection = 'ascending' | 'descending';
type DemandColumn =
  | 'applicationName'
  | 'assignedTo'
  | 'decomState'
  | 'archivalRequired'
  | 'archiveAssessment'
  | 'archiveState'
  | 'intakeCompletionDate';
type ColumnWidths = Record<DemandColumn, number>;

interface ActiveColumnResize {
  column: DemandColumn;
  startX: number;
  startWidth: number;
}

@Component({
  selector: 'app-demands-table',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './demands-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './demands-table.component.scss',
})
export class DemandsTableComponent {
  readonly pageTitle = input.required<string>();
  readonly searchTerm = signal('');
  readonly sortColumn = signal<SortColumn | null>(null);
  readonly sortDirection = signal<SortDirection>('ascending');
  readonly minimumColumnWidths: ColumnWidths = {
    applicationName: 160,
    assignedTo: 140,
    decomState: 120,
    archivalRequired: 180,
    archiveAssessment: 150,
    archiveState: 130,
    intakeCompletionDate: 180,
  };
  readonly columnWidths = signal<ColumnWidths>({
    applicationName: 230,
    assignedTo: 180,
    decomState: 150,
    archivalRequired: 205,
    archiveAssessment: 175,
    archiveState: 150,
    intakeCompletionDate: 222,
  });

  private activeColumnResize: ActiveColumnResize | null = null;

  updateSearch(event: Event): void {
    this.searchTerm.set((event.target as HTMLInputElement).value);
  }

  clearSearch(): void {
    this.searchTerm.set('');
  }

  sortBy(column: SortColumn): void {
    if (this.sortColumn() === column) {
      this.sortDirection.update((direction) =>
        direction === 'ascending' ? 'descending' : 'ascending',
      );
      return;
    }

    this.sortColumn.set(column);
    this.sortDirection.set('ascending');
  }

  ariaSortFor(column: SortColumn): SortDirection | 'none' {
    return this.sortColumn() === column ? this.sortDirection() : 'none';
  }

  isSortAscending(column: SortColumn): boolean {
    return this.sortColumn() === column && this.sortDirection() === 'ascending';
  }

  startColumnResize(event: PointerEvent, column: DemandColumn): void {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.activeColumnResize = {
      column,
      startX: event.clientX,
      startWidth: this.columnWidths()[column],
    };
  }

  moveColumnResize(event: PointerEvent): void {
    const resize = this.activeColumnResize;
    if (!resize) {
      return;
    }

    const width = Math.max(
      this.minimumColumnWidths[resize.column],
      resize.startWidth + event.clientX - resize.startX,
    );
    this.columnWidths.update((widths) => ({ ...widths, [resize.column]: width }));
  }

  endColumnResize(): void {
    this.activeColumnResize = null;
  }

  resizeColumnWithKeyboard(event: KeyboardEvent, column: DemandColumn): void {
    const currentWidth = this.columnWidths()[column];
    const step = event.shiftKey ? 25 : 10;
    let nextWidth: number;

    if (event.key === 'ArrowLeft') {
      nextWidth = currentWidth - step;
    } else if (event.key === 'ArrowRight') {
      nextWidth = currentWidth + step;
    } else if (event.key === 'Home') {
      nextWidth = this.minimumColumnWidths[column];
    } else {
      return;
    }

    event.preventDefault();
    this.columnWidths.update((widths) => ({
      ...widths,
      [column]: Math.max(this.minimumColumnWidths[column], nextWidth),
    }));
  }
}
